import BigNumber from 'bignumber.js';

const DEFAULT_FORMATTER = {
  prefix: '',
  decimalSeparator: '.',
  groupSeparator: ',',
  groupSize: 3,
  secondaryGroupSize: 0,
  fractionGroupSeparator: ' ',
  fractionGroupSize: 0,
  suffix: '',
};
var EMPTYFIELD_TEXT = '-';

export const toSentenceCase = (value: string): string => {
  if (!value) return value;
  const lower = value.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const formatNumber = (
  valstr: any,
  noofzeros: number,
  maxzeros: number,
  replacezero?: boolean,
  showpossign?: boolean,
  showzerosign?: boolean,
  suffix?: string,
  CUST_EMPTYFIELD_TEXT?: string,
) => {
  try {
    let val = new BigNumber(valstr);
    if (val.isNaN())
      return CUST_EMPTYFIELD_TEXT ? CUST_EMPTYFIELD_TEXT : EMPTYFIELD_TEXT;

    if (!replacezero || !val.isZero()) {
      // -1 mean auto-detect
      if (noofzeros === -1) {
        let dotpos = valstr.indexOf('.');
        if (dotpos !== -1) {
          noofzeros = valstr.length - dotpos - 1; // padding the same number of zeros in dp as in the original data
        }
      }

      // if maxzeros is defined, cut the number of zeros accordingly
      if (maxzeros >= 0 && noofzeros > maxzeros) noofzeros = maxzeros;

      let fmt = { ...DEFAULT_FORMATTER };

      if (val.isZero() && showzerosign) {
        fmt.prefix = '±';
      } else if (val.isZero() && showpossign) {
        fmt.prefix = '';
      } else if (val.isPositive() && showpossign) {
        fmt.prefix = '+';
      }

      if (suffix) {
        fmt.suffix = suffix;
      }

      return val.toFormat(noofzeros, BigNumber.ROUND_HALF_UP, fmt);
    } else {
      // 0 -> "-"
      return CUST_EMPTYFIELD_TEXT ? CUST_EMPTYFIELD_TEXT : EMPTYFIELD_TEXT;
    }
  } catch (err) {
    // console.log(err);
    // NaN -> "-"
    return CUST_EMPTYFIELD_TEXT ? CUST_EMPTYFIELD_TEXT : EMPTYFIELD_TEXT;
  }
};

export const formatKMBT = (
  valstr: string,
  noofzeros: number,
  maxzeros: number,
  replacezero: boolean,
) => {
  try {
    let isNegative = false;
    let val = new BigNumber(valstr);

    // Check if the value is invalid
    if (val.isNaN()) {
      return { value: EMPTYFIELD_TEXT, suffix: '' };
    }

    // Handle zero values
    if (val.isZero() && replacezero) {
      return { value: EMPTYFIELD_TEXT, suffix: '' };
    }

    // Skip formatting if value is zero and replacezero is true
    if (val.isZero()) {
      return { value: EMPTYFIELD_TEXT, suffix: '' };
    }
    // if value is negative, convert it to positive and set isNegative to true
    if (val.isNegative()) {
      val = val.abs();
      isNegative = true;
    }

    // Determine the appropriate suffix and divisor
    const suffixes = [
      { threshold: 1000000000000, suffix: 'T' },
      { threshold: 1000000000, suffix: 'B' },
      { threshold: 1000000, suffix: 'M' },
      { threshold: 1000, suffix: 'K' },
    ];

    let formattedVal = val;
    let suffix = '';

    for (const { threshold, suffix: suffixChar } of suffixes) {
      if (val.gte(threshold)) {
        formattedVal = val.dividedBy(threshold);
        suffix = suffixChar;
        break;
      }
    }

    // Apply maxzeros limit if specified
    const finalDecimals =
      maxzeros >= 0 ? Math.min(noofzeros, maxzeros) : noofzeros;

    // Format the number

    //return formattedVal.toFormat(finalDecimals, BigNumber.ROUND_HALF_UP, fmt);
    return {
      value: isNegative
        ? '-' + formattedVal.toFixed(finalDecimals)
        : formattedVal.toFixed(finalDecimals),
      suffix: suffix,
    };
  } catch (err) {
    return { value: EMPTYFIELD_TEXT, suffix: '' };
  }
};

export const roundInterval = (n: number) => {
  let factor: number;
  if (n < 0.000001) factor = 0.000001;
  else if (n < 0.00001) factor = 0.000001;
  else if (n < 0.0001) factor = 0.00001;
  else if (n < 0.001) factor = 0.0001;
  else if (n < 0.01) factor = 0.001;
  else if (n < 0.1) factor = 0.01;
  else if (n < 1) factor = 0.1;
  else if (n < 10) factor = 1;
  else if (n < 100) factor = 10;
  else if (n < 1000) factor = 100;
  else if (n < 10000) factor = 1000;
  else if (n < 100000) factor = 10000;
  else if (n < 1000000) factor = 100000;
  else if (n < 10000000) factor = 1000000;
  else if (n < 100000000) factor = 10000000;
  else factor = 100000000;
  return ((n / factor + 0.6) | 0) * factor; // The rounding is 40% up
};

export const round = (num: string | number, decimalPlaces = 0) => {
  num = Math.round(Number(num + 'e' + decimalPlaces));
  return Number(num + 'e' + -decimalPlaces);
};

export const countDecimals = (num: number) => {
  if (Math.floor(num) === num) return 0;
  return num.toString().split('.')[1].length || 0;
};

export const roundToNearestMultiple = (
  number: string | number,
  step: number,
) => {
  return Math.round(Number(number) / step) * step;
};

export const getExponential = (value: number, decimalPlaces: number) => {
  return value === 0
    ? 0
    : value < 0
      ? +(-(10 ** -value)).toFixed(decimalPlaces)
      : +(10 ** value).toFixed(decimalPlaces);
};
export const getLogarithm = (value: number, decimalPlaces?: number) => {
  return value === 0
    ? 0
    : value < 0
      ? -+Math.log10(-value).toFixed(4)
      : +Math.log10(value).toFixed(4);
};

export const formatLocaleNumber = (
  val: number | string | null | undefined,
  dp?: number,
  locale?: string,
) => {
  if (!val && val !== 0) return '-';
  try {
    if (typeof val === 'string') {
      if (Number.isNaN(+val)) return val;
      if (!dp && dp !== 0) return Number(val).toLocaleString();
      return Number(val).toLocaleString(locale, {
        minimumFractionDigits: dp,
        maximumFractionDigits: dp,
      });
    }
    // console.log(dp, locale);
    if (!dp && dp !== 0) return val.toLocaleString();

    return val.toLocaleString(locale, {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    });
  } catch (e) {
    return '-';
  }
};

export const shortenNumber = (val: number | string) => {
  if (typeof val !== 'number' || Number.isNaN(+val)) return '-';
  const data = Number(val);
  if (data < 10 * 6) return data;

  if (data >= 10 ** 12) {
    return `${formatLocaleNumber(data / 10 ** 12, 3)}`;
  }
  if (data >= 10 ** 9 && data < 10 ** 12) {
    return `${formatLocaleNumber(data / 10 ** 9, 3)}`;
  }
  if (data >= 10 ** 6 && data < 10 ** 9) {
    return `${formatLocaleNumber(data / 10 ** 6, 3)}`;
  }
  return `${formatLocaleNumber(data)}`;
};
export const getValUnit = (data: number | string | null) => {
  if ((!data && data !== 0) || Number.isNaN(Number(data))) return '';
  const val = typeof data === 'number' ? data : +data;
  if (val >= 10 ** 12) {
    return 'T';
  }
  if (val >= 10 ** 9 && val < 10 ** 12) {
    return 'B';
  }
  if (val >= 10 ** 6 && val < 1000000000) {
    return 'M';
  }
  return '';
};

export const logValue2TrueValue = (logval: number, truemid: number) => {
  return (logval > 0 ? 1 : -1) * (Math.exp(Math.abs(logval)) - 1) + truemid;
};

export const truevalue2logvalue = (trueval: number, truemid: number) => {
  const value =
    (trueval - truemid > 0 ? 1 : -1) *
    Math.log(Math.abs(trueval - truemid) + 1);
  return parseFloat(value.toString());
};

export function getUpDownClass(value: number | null | undefined): string {
  if (value == null) return '';
  const rounded = Number(Number(value).toFixed(2));
  if (rounded > 0) return 'positive';
  if (rounded < 0) return 'negative';
  return 'nochange';
}

export const getCssVariable = (
  name: string,
  theme?: string,
  fallback: string = '',
): string => {
  if (theme) {
    document.documentElement.setAttribute('data-theme', theme);
  }
  return (
    getComputedStyle(document.documentElement).getPropertyValue(name).trim() ||
    fallback
  );
};

export const getAxisTickConfig = (
  values: number[],
  targetTicks = 5,
): { max: number; interval: number } => {
  const maxValue = values.length ? Math.max(...values) : 0;
  const rawInterval = maxValue / targetTicks || 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const normalized = rawInterval / magnitude;
  const niceMultiplier =
    normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 5 ? 5 : 10;
  const interval = niceMultiplier * magnitude;
  const max = Math.ceil(maxValue / interval) * interval;
  return { max, interval };
};

export const getMappingColorChart = (theme: string): string[] => [
  getCssVariable('--chart-primary', theme),
  getCssVariable('--chart-secondary', theme),
  getCssVariable('--chart-tertiary', theme),
  getCssVariable('--chart-quaternary', theme),
  getCssVariable('--chart-quinary', theme),
  getCssVariable('--chart-senary', theme),
];

export type SentimentTone = 'up' | 'down' | 'neutral';

export interface SentimentLevel {
  label: string;
  tone: SentimentTone;
}

const EMPTY_SENTIMENT_LEVEL: SentimentLevel = { label: '-', tone: 'neutral' };

export const getSentimentLevel = (
  value?: string | number | null,
): SentimentLevel => {
  if (!value || Number.isNaN(value) || value === '-')
    return EMPTY_SENTIMENT_LEVEL;

  if (+value >= 0.76) return { label: 'Extremely Bullish', tone: 'up' };
  if (+value >= 0.26) return { label: 'Bullish', tone: 'up' };
  if (+value >= -0.25) return { label: 'Neutral', tone: 'neutral' };
  if (+value >= -0.75) return { label: 'Bearish', tone: 'down' };
  return { label: 'Extremely Bearish', tone: 'down' };
};

export const getSentimentLabel = (value: string | number | null): string =>
  getSentimentLevel(value).label;

export const getDecimalPlaces = (currency?: string | null): number => {
  if (currency === 'USD') return 4;
  if (currency === 'AUD') return 3;
  return 2;
};
